/**
 * Validation infrastructure for view model validations: This
 * toolset allows validation of reactive values, e.g.
 * check for empty entries, min/max numbers etc.
 *
 * It is meant to be used in a user form to indicate
 * wrong/missing values.
 *
 * Usage:
 *
 * - Create one or more validators with `createValidator` from a reactive value
 * - optionally create a summary validator info with `createValidators`: This will
 *   allow to check if all the given validators are valid (e.g. to invalidate a form submit btn if a value is missing)
 * - use the `isValid` and `msg` ref variables from a validator to indicate view field errors
 *
 * Example: Create a single validator of a ref value:
 * <code>
 * const myValue = ref('');
 * const notEmptyValidator = createValidator(myValue, [validateNotEmpty]);
 *
 * watch(notEmptyValidator.isValid, (isValid) => console.log(isValid));
 * <template>
 * My val is valid: {{notEmptyValidator.isValid}}
 * </template>
 * </code>
 *
 * Example: Create multiple validators of some values:
 * <code>
 * const myValue1 = ref('');
 * const myValue2 = ref(0);
 * const {validators, allValid} = createValidators([
 *      myValue1: createValidator(myValue1, [validateMinLength(5)]),
 *      myValue2: createValidator(myValue2, [validateNumberBetween(1,100)]),
 * ]);
 * watch(allValid, (isValid) => console.log('all valid', isValid));
 * <template>
 * My val1 is valid: {{validators.myValue1.isValid}}, {{validators.myValue1.msg}}
 * My val2 is valid: {{validators.myValue2.isValid}}, {{validators.myValue2.msg}}
 * All valid: {{allValid}}
 * </template>
 * </code>
 */
import { type Dayjs } from 'dayjs';
import { watch, type Ref, ref, computed, toValue, type ComputedRef, nextTick } from 'vue';

export type FieldValidatorFn<T> = (value: T) => true | string;
export type FieldValidatorInfo = {
    msg: Ref<string | null>;
    isValid: Ref<boolean>;
    /** If false, the validation does not happen, the value is always valid. Can be set / read */
    enabled: Ref<boolean>;
    /** manually validate the value: this function will trigger the validation of that value */
    validate: () => void;
    reset: () => Promise<void>;
};

export type ValidatorOptions = {
    initialValidate?: boolean;
    enabled?: boolean;
};

export function createValidator<T>(
    refVal: Ref<T>,
    rules: Array<FieldValidatorFn<T>>,
    opts?: ValidatorOptions,
): FieldValidatorInfo {
    opts = { ...{ initialValidate: true, enabled: true }, ...opts };
    const enabled: Ref<boolean> = ref(opts.enabled!);
    const isValid = ref(true);
    const msg: Ref<string | null> = ref(null);

    watch(refVal, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            validate(newVal);
        }
    });
    watch(enabled, (isEnabled) => {
        if (!isEnabled) {
            isValid.value = true;
            msg.value = null;
        }
    });

    const reset = async () => {
        /** resets validator by setting isValid to true */
        await nextTick(() => {
            isValid.value = true;
        });
    };

    const validate = (val: any) => {
        // Only validate if we are enabled. If not,
        // set the value to valid and return early:
        if (!enabled.value) {
            isValid.value = true;
            msg.value = null;
            return;
        }

        let allValid = true;
        let error: string | null = null;

        for (const r of rules) {
            const err = r(val);
            if (err !== true) {
                allValid = false;
                error = err || 'Invalid value';
                break;
            }
        }
        isValid.value = allValid;
        msg.value = allValid ? null : error;
    };
    // initial validate:
    if (opts.initialValidate) {
        validate(refVal.value);
    }
    return {
        msg,
        isValid,
        enabled,
        validate: () => validate(refVal.value),
        reset,
    };
}

export type FieldValidators = { [key: string]: FieldValidatorInfo };
export type FieldValidatorsInfo = {
    allValid: Ref<boolean>;
    validators: FieldValidators;
    invalidValidators: ComputedRef<FieldValidators>;
    /** manually validate all validators: this function will trigger the validation of all configured validators */
    validateAll: () => void;
    /** enables / disables all the assigned validators at once. Note that the validation does not start automatically when enabled. */
    enableAll: (setEnabled?: boolean) => void;
    /** resets all validators by setting isValid to true */
    resetAll: () => Promise<void>;
};
export function createValidators(validations: FieldValidators): FieldValidatorsInfo {
    const validators: FieldValidators = { ...validations };
    const allValidRef = computed(() => {
        for (const validatorKey in validations) {
            const validator = validations[validatorKey];
            if (validator.isValid.value !== true) {
                return false;
            }
        }
        return true;
    });
    const invalidValidators: ComputedRef<FieldValidators> = computed(() => {
        const res: FieldValidators = {};
        for (const validatorKey in validators) {
            const validator = validators[validatorKey];
            if (toValue(validator.isValid) !== true) {
                res[validatorKey] = validator;
            }
        }

        return res;
    });
    const validateAll = () => {
        for (const validator of Object.values(validators)) {
            validator.validate();
        }
    };

    const enableAll = (setEnabled: boolean = true) => {
        for (const validator of Object.values(validators)) {
            validator.enabled.value = setEnabled;
        }
    };

    const resetAll = async () => {
        await nextTick(() => {
            for (const validator of Object.values(validators)) {
                validator.isValid.value = true;
            }
        });
    };

    return {
        allValid: allValidRef,
        validators,
        invalidValidators,
        validateAll,
        enableAll,
        resetAll,
    };
}

/** -------------------- Validator Functions ----------------- */

/**
 * checks if a value is not empty (length, or not null)
 * Numbers are always valid (even 0).
 *
 * @param val
 * @param errMsg
 * @returns true if the value is not empty, the error message otherwise
 */
export const validateNotEmpty = function (val: any | null, errMsg: string = 'Darf nicht leer sein'): true | string {
    if (val === true || val === false) {
        return true;
    }
    if (typeof val === 'string') {
        return val.trim().length === 0 ? errMsg : true;
    }
    if (val instanceof Array) {
        return val.length === 0 ? errMsg : true;
    }
    if (typeof val === 'number') {
        return isNaN(val) ? errMsg : true;
    }
    return errMsg;
};

/**
 * checks if a value is not null or undefined
 *
 * @param val
 * @param errMsg
 * @returns true if the value is not null/undefined, the error message otherwise
 */
export const validateNotNull = function (val: any, errMsg: string = 'Darf nicht leer sein'): true | string {
    if (val === null || val === undefined) {
        return errMsg;
    }
    return true;
};

/**
 * Validates a Dayjs object: Checks if the Dayjs.isValid() function returns true.
 *
 * @param val
 * @param errMsg
 * @returns true if the value is valid, the error message otherwise
 */
export const validateDayjs = function (val: Dayjs | null, errMsg: string = 'Datum erwartet'): true | string {
    if (val === null) {
        return true;
    }
    const v = toValue(val);
    if (v?.isValid) {
        return v.isValid() ? true : errMsg;
    }
    return errMsg;
};

/**
 * Validates a string if it is of a given minimum length. This function
 * is a validate factory, so call it with a min returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the given min length
 */
export const validateMinLength =
    (n: number, errMsg: string = 'Mind. {n} Zeichen') =>
    (val: string | number | null): true | string => {
        if (val === null) {
            return true;
        }
        const strVal = String(val || '');
        if (strVal.length >= n) {
            return true;
        }
        return errMsg.replace('{n}', String(n));
    };

/**
 * Validates a string if it is of a given maximum length. This function
 * is a validate factory, so call it with a max returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the given max length
 */
export const validateMaxLength =
    (n: number, errMsg: string = 'Max. {n} Zeichen') =>
    (val: string | number | null): true | string => {
        if (val === null) {
            return true;
        }
        const strVal = String(val || '');
        if (strVal.length <= n) {
            return true;
        }
        return errMsg.replace('{n}', String(n));
    };

/**
 * Validates a number if it is of a given minimum value. This function
 * is a validate factory, so call it with a min returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the given min value
 */
export const validateNumberMin =
    (n: number, errMsg: string = 'Wert mind. {n}') =>
    (val: string | number | null): true | string => {
        if (val === null) {
            return true;
        }
        const nrVal = Number(val);
        if (nrVal >= n) {
            return true;
        }
        return errMsg.replace('{n}', String(n));
    };

/**
 * Validates a number if it is of a given maximum value. This function
 * is a validate factory, so call it with a min returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the given max value
 */
export const validateNumberMax =
    (n: number, errMsg: string = 'Wert max. {n}') =>
    (val: string | number | null): true | string => {
        if (val === null) {
            return true;
        }
        const nrVal = Number(val);
        if (nrVal <= n) {
            return true;
        }
        return errMsg.replace('{n}', String(n));
    };

/**
 * Validates a number if it is between two values (including the values). This function
 * is a validate factory, so call it with a min returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the given boundary values
 */
export const validateNumberBetween =
    (nMin: number, nMax: number, errMsg: string = 'Zahl muss zwischen {nMin} und {nMax} liegen.') =>
    (val: string | number | null): true | string => {
        if (val === null) {
            return true;
        }
        const nrVal = Number(val);
        if (nrVal >= nMin && nrVal <= nMax) {
            return true;
        }
        return errMsg.replace('{nMin}', String(nMin)).replace('{nMax}', String(nMax));
    };

/**
 * Validates a string if it matches a given regular expression pattern. This function
 * is a validate factory, so call it with a min returns a validator function.
 *
 * @param val
 * @param errMsg
 * @returns validator function configured with the regular expression
 */
export const validateRegex =
    (pattern: RegExp, errMsg: string = 'Ungültige Zeichenkette') =>
    (val: string | null): true | string => {
        if (val === null) {
            return true;
        }
        return val ? pattern.test(val) || errMsg : errMsg;
    };

/**
 * Validates a string if it is an email address.
 *
 * @param val
 * @param errMsg
 * @returns true if the value is a valid email address, the error message otherwise
 */
export const validateEmail = (val: string | null, errMsg: string = 'Ungültige E-Mail') => {
    if (val === null) {
        return true;
    }
    const pattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return validateRegex(pattern, errMsg)(val);
};

/**
 * Validates a string if it is a valid URL
 *
 * @param val
 * @param errMsg
 * @returns true if the value is a valid url address, the error message otherwise
 */
export const validateUrl = (val: string | null, errMsg: string = 'Ungültige URL') => {
    if (val === null) {
        return true;
    }
    const pattern =
        /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www\.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w\-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[.!/\\\w]*))?)/;
    return validateRegex(pattern, errMsg)(val);
};

/**
 * Validates if an array contains at least one non-falsy element
 *
 * @returns true if the given value contains at least one non-falsy element
 */
export const validateNonFalsyArray = (
    val: any[] | null | undefined,
    errMsg: string = 'Bitte mind. 1 Wert auswählen',
) => {
    if (val instanceof Array) {
        return val.filter(Boolean).length > 0 ? true : errMsg;
    }
    return errMsg;
};
